import { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import { parseCanvasJson, type CanvasDocument } from '@orqforge/shared';
import { apiClient } from '../lib/api-client';
import { authHeaders } from '../lib/http';
import { CanvasPane } from '../components/collaborate/CanvasPane';
import { readCanvasText } from '../components/collaborate/canvas-files';
import { Button } from '../components/ui/Button';

interface CanvasItem {
  id: string;
  filename: string;
  url?: string | null;
  runId?: string | null;
  contentBase64?: string;
}

export function ReportsPage() {
  const [items, setItems] = useState<CanvasItem[]>([]);
  const [document, setDocument] = useState<CanvasDocument | null>(null);
  const [selectedId, setSelectedId] = useState<string | null>(null);
  const [selectedRunId, setSelectedRunId] = useState<string | null>(null);
  const [canvasBusy, setCanvasBusy] = useState(false);

  useEffect(() => {
    void apiClient.listCanvases().then((rows) => {
      setItems(rows);
      if (rows[0]) void open(rows[0]);
    }).catch(() => setItems([]));
  }, []);

  async function open(item: CanvasItem) {
    setSelectedId(item.id);
    setSelectedRunId(item.runId ?? null);
    const inline = readCanvasText(item);
    if (inline !== null) {
      setDocument(parseCanvasJson(inline));
      return;
    }
    if (!item.url) {
      setDocument(null);
      return;
    }
    const headers = await authHeaders();
    const res = await fetch(item.url, { headers });
    setDocument(parseCanvasJson(await res.text()));
  }

  async function onCanvasAction(event: { toolKey: string; arguments: Record<string, unknown> }) {
    setCanvasBusy(true);
    try {
      const res = await apiClient.executeTool(event.toolKey, event.arguments);
      if (res.canvas) setDocument(res.canvas);
      setItems(await apiClient.listCanvases());
    } finally {
      setCanvasBusy(false);
    }
  }

  return (
    <div>
      <h1 className="text-2xl font-semibold">Reports</h1>
      <p className="text-sm text-gray-500">Canvas artifacts from runs in this workspace.</p>
      <div className="mt-5 grid gap-4 lg:grid-cols-[minmax(280px,360px)_minmax(0,1fr)]">
        <aside className="rounded-xl border border-gray-200 bg-gray-50 p-3 dark:border-gray-700 dark:bg-gray-800/40">
          {items.length === 0 && <p className="p-3 text-sm text-gray-500">No canvases yet.</p>}
          <ul className="space-y-1">
            {items.map((item) => (
              <li key={item.id}>
                <Button type="button" variant={selectedId === item.id ? 'secondary' : 'ghost'} size="sm" className="w-full justify-start" data-testid={`report-item-${item.id}`} onClick={() => void open(item)}>
                  {item.filename}
                </Button>
              </li>
            ))}
          </ul>
        </aside>
        <div className="min-h-[24rem]">
          {document && <CanvasPane document={document} executing={canvasBusy} onAction={(event) => void onCanvasAction(event)} />}
          {selectedRunId && (
            <Link className="mt-2 inline-block text-sm text-blue-600" to={`/runs?runId=${selectedRunId}`}>Open run</Link>
          )}
        </div>
      </div>
    </div>
  );
}
